import React from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';
import SectionContent from '../lab/SectionContent';

const ExamQCMSolution = () => {
    const { t } = useTranslation();
    const { currentQCM } = useQCM();

    if (!currentQCM || !currentQCM.qcm) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-400">{t('no_solution_available')}</p>
            </div>
        );
    }

    // Gérer les réponses correctes (simple ou multiples)
    const normalize = (opt) => {
        if (!opt) return null;
        if (typeof opt === 'string') return { answer: opt };
        if (opt && typeof opt.answer === 'string') return opt;
        return null;
    };
    
    const correctSingles = Array.isArray(currentQCM.qcm.correct_answer)
        ? currentQCM.qcm.correct_answer.map(normalize).filter(Boolean)
        : (currentQCM.qcm.correct_answer ? [normalize(currentQCM.qcm.correct_answer)].filter(Boolean) : []);
    const correctMultis = Array.isArray(currentQCM.qcm.correct_answers) 
        ? currentQCM.qcm.correct_answers.map(normalize).filter(Boolean) : [];
    
    const correctAnswers = [...correctSingles, ...correctMultis];
    const distractors = currentQCM.qcm.distractors || [];

    return (
        <div className="space-y-6">
            {/* Question Reminder */}
            <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                    Question
                </h3>
                <div className="text-gray-200">
                    <SectionContent content={currentQCM.qcm.question} />
                </div>
            </div>

            {/* Correct Answer(s) */}
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {correctAnswers.length > 1 ? 'Correct Answers' : 'Correct Answer'}
                </h3>
                
                {correctAnswers.map((correctAnswer, index) => (
                    <div key={index} className="mb-4 last:mb-0">
                        <div className="bg-gray-800 rounded p-3 mb-3">
                            <p className="text-gray-200 font-medium">
                                {correctAnswer.answer}
                            </p>
                        </div>

                        {correctAnswer.explanation && (
                            <div>
                                <h4 className="text-md font-semibold text-green-300 mb-2">
                                    Explanation:
                                </h4>
                                <div className="text-gray-200 leading-relaxed">
                                    <SectionContent content={correctAnswer.explanation} />
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Incorrect Options */}
            {distractors.length > 0 && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Why Other Options Are Incorrect
                    </h3>
                    
                    <div className="space-y-4">
                        {distractors.map((distractor, index) => (
                            <div key={index} className="bg-gray-800 rounded p-3">
                                <div className="flex items-start gap-3 mb-2">
                                    <div className="mt-1">
                                        <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-gray-100 font-medium">{distractor.answer}</div>
                                    </div>
                                </div>
                                {distractor.explanation && (
                                    <div className="text-gray-300 text-lg mt-2">
                                        <span className="font-semibold text-red-300 mr-2">Why it's not correct:</span>
                                        <SectionContent content={distractor.explanation} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tips Section */}
            {currentQCM?.tips && (
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-purple-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        Tips
                    </h3>
                    <div className="bg-gray-800 rounded p-3">
                        <p className="text-gray-300 leading-relaxed">
                            <SectionContent content={currentQCM.tips} />
                        </p>
                    </div>
                </div>
            )}

            {/* Common Error */}
            {currentQCM?.common_error && (
                <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-orange-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.768 0L3.045 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        Common Error
                    </h3>
                    <p className="text-gray-300">
                        <SectionContent content={currentQCM.common_error} />
                    </p>
                </div>
            )}
        </div>
    );
};

export default ExamQCMSolution;
